import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink, Landmark, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";

/**
 * Phase 114 — app-wide strip shown ONLY while a Basiq bank connection needs re-authorising
 * (`/bank-feeds/alerts`), i.e. the live feed into Transactions has silently stopped. Mirrors the
 * Xero strip: admins only, dismissable for the session, re-checks every few minutes, and the
 * Re-authorise button opens Basiq's Consent UI in a new tab then polls until the bank is active again.
 */
const STRIP_DISMISS_KEY = "bf_bankfeed_strip_dismissed";

export const BankFeedReconnectBanner = ({ className = "" }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(STRIP_DISMISS_KEY) === "1");
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get("/bank-feeds/alerts");
      setAlerts(res.data || null);
    } catch {
      setAlerts(null); // not connected / not an editor — nothing to show
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);
  useEffect(() => {
    if (!isAdmin) return undefined;
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [isAdmin, load]);

  // a fresh outage after a dismissal should show again — key the dismissal to the connection set
  const first = alerts?.needs_reauth?.[0];
  if (!isAdmin || !alerts?.count || !first) return null;
  if (dismissed || location.pathname.startsWith("/settings")) return null;

  const reauth = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bank-feeds/connections/${first.connection_id}/reauth`);
      const w = window.open(data.consent_url, "_blank", "noopener");
      if (!w) {
        window.location.href = data.consent_url;
        return;
      }
      toast.info("Finish the bank login in the new tab — this strip clears itself once the bank is active again");
      let tries = 0;
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const res = await api.get("/bank-feeds/alerts");
          if (!res.data?.count) {
            clearInterval(pollRef.current);
            setAlerts(res.data);
            setBusy(false);
            toast.success("Bank reconnected — the feed is flowing again");
            return;
          }
        } catch {
          /* keep polling */
        }
        if (tries > 75) {
          clearInterval(pollRef.current);
          setBusy(false);
        }
      }, 4000);
    } catch (e) {
      toast.error(errMsg(e, "Could not start re-authorisation"));
      setBusy(false);
    }
  };

  const bank = first.institution_name || "Your bank";
  const more = alerts.count > 1 ? ` and ${alerts.count - 1} other connection${alerts.count > 2 ? "s" : ""}` : "";
  const since = first.since ? ` since ${fmtDate(first.since)}` : "";

  return (
    <div
      role="alert"
      className={`z-[45] flex items-center gap-3 border-b border-amber-500/40 bg-amber-500/10 px-4 py-1.5 text-amber-100 sm:px-6 lg:px-8 ${className}`}
      data-testid="bank-feed-reconnect-strip"
      data-count={alerts.count}
    >
      <Landmark className="h-4 w-4 shrink-0 text-amber-300" />
      <p className="min-w-0 flex-1 truncate text-xs" data-testid="bank-feed-reconnect-strip-text">
        <span className="font-semibold text-amber-200">{bank} bank feed stopped</span>
        <span className="text-amber-200/80">
          {since}
          {more} — {first.accounts} account{first.accounts === 1 ? "" : "s"} no longer sync into Transactions until you re-authorise.
        </span>
      </p>
      <Button size="sm" className="h-7 text-xs" onClick={reauth} disabled={busy} data-testid="bank-feed-reconnect-strip-button">
        {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1 h-3.5 w-3.5" />} Re-authorise
      </Button>
      <Button asChild size="sm" variant="ghost" className="h-7 text-xs text-amber-200 hover:text-amber-100">
        <Link to="/settings?tab=bank-feeds" data-testid="bank-feed-reconnect-strip-settings">Bank feeds</Link>
      </Button>
      <button
        type="button"
        aria-label="Hide for this session"
        className="rounded p-1 text-amber-200/70 transition-colors hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => {
          sessionStorage.setItem(STRIP_DISMISS_KEY, "1");
          setDismissed(true);
        }}
        data-testid="bank-feed-reconnect-strip-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
